/**
 * RuleBuilderStep - Step 2 of view creation wizard
 *
 * File selection rules with preview functionality
 */

import React, { useState } from 'react';
import { Eye, FileText, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { usePreviewRules } from '@/hooks/useViews';
import { useToast } from '@/hooks/useToast';
import type { ViewFormData } from '@/pages/ViewCreatePage';
import { RuleBuilder } from './RuleBuilder';
import { FILE_BASED_RULE_TYPES } from './fileBasedRuleTypes';

interface RuleBuilderStepProps {
  formData: ViewFormData;
  updateFormData: (updates: Partial<ViewFormData>) => void;
  /** View names to exclude from ViewSelector (e.g. to prevent self-reference) */
  excludeViewNames?: string[];
}

export const RuleBuilderStep: React.FC<RuleBuilderStepProps> = ({ formData, updateFormData, excludeViewNames }) => {
  const { toast } = useToast();
  const previewRules = usePreviewRules();
  const [previewData, setPreviewData] = useState<{
    matching_files: number;
    estimated_rows: number;
    sample_files: string[];
  } | null>(null);
  const [showSampleFiles, setShowSampleFiles] = useState(false);

  /**
   * Clear all column-dependent form configurations.
   * Called when the data source changes (type switch or view selection change)
   * because different sources have different column schemas.
   */
  const clearColumnConfigurations = () => {
    updateFormData({
      columns: [],
      select_all_columns: true,
      where_conditions: [],
      group_by: null,
      having_conditions: [],
      joins: [],
    });
  };

  /**
   * Called when the user confirms switching between file-based and view-based source
   */
  const handleSourceTypeChange = () => {
    clearColumnConfigurations();
    toast({
      title: 'Column Configurations Cleared',
      description: 'Column configurations cleared due to source type change.',
    });
  };

  /**
   * Called when the user selects a different source view within view mode
   */
  const handleSourceViewChange = () => {
    clearColumnConfigurations();
    toast({
      title: 'Column Configurations Cleared',
      description: 'Column configurations cleared due to source view change.',
    });
  };

  /**
   * Handle preview button click
   */
  const handlePreview = async () => {
    if (formData.rules.length === 0) {
      toast({
        title: 'No Rules',
        description: 'Please add at least one rule before previewing.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const result = await previewRules.mutateAsync({
        rules: formData.rules,
        rule_logic: formData.rule_logic,
      });
      setPreviewData(result);
      toast({
        title: 'Preview Generated',
        description: `Found ${result.matching_files} matching files`,
      });
    } catch (error: any) {
      toast({
        title: 'Preview Failed',
        description: error?.response?.data?.message || error.message || 'Failed to preview rules',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Rule Builder */}
      <RuleBuilder
        rules={formData.rules}
        onRulesChange={(rules) => updateFormData({ rules })}
        ruleLogic={formData.rule_logic}
        onRuleLogicChange={(logic) => updateFormData({ rule_logic: logic })}
        excludeViewNames={excludeViewNames}
        onSourceTypeChange={handleSourceTypeChange}
        onSourceViewChange={handleSourceViewChange}
      />

      {/* Preview Button */}
      <div className="flex justify-center">
        <Button
          type="button"
          variant="secondary"
          onClick={handlePreview}
          disabled={formData.rules.length === 0 || previewRules.isPending}
        >
          <Eye className="h-4 w-4 mr-2" />
          {previewRules.isPending ? 'Previewing...' : 'Preview Matching Files'}
        </Button>
      </div>

      {/* Preview Results */}
      {previewData && (
        <Card className="bg-muted/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Preview Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 mb-4">
              {/* Matching Files */}
              <div className="p-4 bg-background rounded-lg border">
                <div className="text-3xl font-bold text-primary">
                  {previewData.matching_files.toLocaleString()}
                </div>
                <div className="text-sm text-muted-foreground mt-1">Matching Files</div>
              </div>

              {/* Estimated Rows */}
              <div className="p-4 bg-background rounded-lg border">
                <div className="text-3xl font-bold text-primary">
                  {previewData.estimated_rows >= 1000000
                    ? `${(previewData.estimated_rows / 1000000).toFixed(1)}M`
                    : previewData.estimated_rows >= 1000
                    ? `${(previewData.estimated_rows / 1000).toFixed(1)}K`
                    : previewData.estimated_rows.toLocaleString()}
                </div>
                <div className="text-sm text-muted-foreground mt-1">Estimated Rows</div>
              </div>
            </div>

            {/* Sample Files */}
            {previewData.sample_files.length > 0 && (
              <div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSampleFiles(!showSampleFiles)}
                  className="mb-2"
                >
                  {showSampleFiles ? 'Hide' : 'Show'} Sample Files ({previewData.sample_files.length})
                </Button>

                {showSampleFiles && (
                  <div className="mt-2 p-4 bg-background rounded-lg border max-h-60 overflow-y-auto">
                    <ul className="space-y-1 text-sm font-mono">
                      {previewData.sample_files.map((file, index) => (
                        <li key={index} className="text-muted-foreground">
                          {file}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Warning for no matches */}
            {previewData.matching_files === 0 && (
              <Alert variant="destructive" className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  No files match the current rules. Please adjust your rules to match at least one file.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Help Text */}
      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <h4 className="font-medium mb-2">Source Options</h4>
        <ul className="text-sm text-muted-foreground space-y-2">
          <li>
            <strong>Build From View:</strong> Use an existing view as the data source (cannot be combined with file-based rules)
          </li>
        </ul>
        <h4 className="font-medium mb-2 mt-4">File-Based Rule Types</h4>
        <ul className="text-sm text-muted-foreground space-y-2">
          {FILE_BASED_RULE_TYPES.map(({ value, label, description }) => (
            <li key={value}>
              <strong>{label}:</strong> {description}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
